import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { PostsCacheContext } from '../App';
import './Home.css';
import Header from './Header';
import { FaRegComment, FaHeart, FaRegHeart, FaTimes, FaTrash } from 'react-icons/fa';
import { BsEmojiSmile } from 'react-icons/bs';
import { FiSend } from 'react-icons/fi';
import p from './images/profile.jpg';
import { useNavigate, useParams } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import Chatbot from "./Chatbot";

// ─────────────────────────────────────────────────────────
// IMPORTANT: These components are defined OUTSIDE Home so
// React never unmounts/remounts them on parent re-renders,
// which was the root cause of the image blinking.
// ─────────────────────────────────────────────────────────

const CommentInput = React.memo(({ postid, onSubmit, userAvatar }) => {
    const [commentText, setCommentText] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const handleEmojiClick = (emojiObject) => {
        setCommentText(prev => prev + emojiObject.emoji);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (commentText.trim()) {
            onSubmit(postid, commentText);
            setCommentText('');
            setShowEmojiPicker(false);
        }
    };

    return (
        <form className="comment-input-container" onSubmit={handleSubmit}>
            <img
                src={userAvatar || p}
                alt="You"
                className="comment-user-img"
            />
            <div className="comment-input-wrapper">
                <input
                    type="text"
                    placeholder="Add a comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="comment-input"
                />
                <div className="comment-input-actions">
                    <button
                        type="button"
                        className="emoji-btn"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                        <BsEmojiSmile />
                    </button>
                    <button
                        type="submit"
                        className="post-comment-btn"
                        disabled={!commentText.trim()}
                    >
                        Post
                    </button>
                </div>
                {showEmojiPicker && (
                    <div className="emoji-picker-wrapper">
                        <div className="emoji-picker-header">
                            <button
                                type="button"
                                className="close-emoji-picker"
                                onClick={() => setShowEmojiPicker(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <EmojiPicker onEmojiClick={handleEmojiClick} />
                    </div>
                )}
            </div>
        </form>
    );
});

const ReplyInput = React.memo(({ commentid, onReplySubmit, onClose }) => {
    const [replyText, setReplyText] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const handleEmojiClick = (emojiObject) => {
        setReplyText(prev => prev + emojiObject.emoji);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (replyText.trim()) {
            onReplySubmit(commentid, replyText);
            setReplyText('');
            setShowEmojiPicker(false);
        }
    };

    return (
        <form className="reply-input-container" onSubmit={handleSubmit}>
            <input
                type="text"
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="reply-input"
            />
            <div className="reply-input-actions">
                <button
                    type="button"
                    className="emoji-btn"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                    <BsEmojiSmile />
                </button>
                <button
                    type="submit"
                    className="send-reply-btn"
                    disabled={!replyText.trim()}
                >
                    <FiSend />
                </button>
                <button
                    type="button"
                    className="close-reply-btn"
                    onClick={onClose}
                >
                    <FaTimes />
                </button>
            </div>
            {showEmojiPicker && (
                <div className="emoji-picker-wrapper">
                    <div className="emoji-picker-header">
                        <button
                            type="button"
                            className="close-emoji-picker"
                            onClick={() => setShowEmojiPicker(false)}
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                </div>
            )}
        </form>
    );
});

// ─────────────────────────────────────────────────────────
// Post card — also outside Home to keep identity stable
// ─────────────────────────────────────────────────────────

const Post = React.memo(({
    post,
    likeCount,
    followText,
    profileImages,
    showComments,
    comments,
    commentsLoading,
    activeReplies,
    expandedReplies,
    userId,
    onLike,
    onFollow,
    onShowComments,
    onCommentSubmit,
    onReplySubmit,
    onToggleReply,
    onToggleRepliesVisibility,
    onShowDeleteComment,
    onShowDeleteReply,
    postRef,
}) => {
    const navigate = useNavigate();
    const postComments = comments[post.postid] || [];

    const handlePostClick = () => navigate(`/store/${post.page_id}`);

    return (
        <div
            className="linkedin-post"
            ref={postRef}
        >
            <div className="post-header">
                <div className="post-user" onClick={handlePostClick}>
                    <img
                        src={profileImages[post.page_id] || p}
                        alt="Profile"
                        className="post-profile-img"
                    />
                    <div className="post-user-info">
                        <span className="post-username">{post.page_name}</span>
                    </div>
                </div>
                <button
                    className="follow-btn-home"
                    onClick={(e) => {
                        e.stopPropagation();
                        onFollow(post.page_id);
                    }}
                    disabled={!userId}
                >
                    {followText[post.page_id] || "+ Follow"}
                </button>
            </div>

            <div className="post-content">
                <p className="post-text">{post.post_content}</p>
                {post.image_url && (
                    <img
                        src={post.image_url}
                        alt="Post"
                        className="post-image"
                        loading="lazy"
                    />
                )}
                {post.video_url && (
                    <video controls className="post-video">
                        <source src={post.video_url} type="video/mp4" />
                    </video>
                )}
            </div>

            <div className="post-stats">
                <span>{likeCount[post.postid] || 0} likes</span>
                <span>{postComments.length} comments</span>
            </div>

            <div className="post-actions">
                <button
                    className={`action-btn-home ${likeCount[post.postid] > 0 ? 'liked' : ''}`}
                    onClick={() => onLike(post.postid)}
                    type="button"
                >
                    {likeCount[post.postid] > 0 ? <FaHeart /> : <FaRegHeart />}
                    <span>Like</span>
                </button>
                <button
                    className="action-btn-home"
                    onClick={() => onShowComments(post.postid)}
                    type="button"
                >
                    <FaRegComment />
                    <span>Comment</span>
                </button>
            </div>

            {showComments[post.postid] && (
                <div className="comments-section">
                    <div className="comments-header">
                        <h4>Comments ({postComments.length})</h4>
                        <button
                            className="close-comments-btn"
                            onClick={() => onShowComments(post.postid)}
                            type="button"
                            aria-label="Close comments"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div className="comments-list">
                        {commentsLoading[post.postid] && (
                            <div className="comment-loading-skeleton">
                                <div className="skeleton-avatar"></div>
                                <div className="skeleton-content">
                                    <div className="skeleton-line"></div>
                                    <div className="skeleton-line short"></div>
                                </div>
                            </div>
                        )}
                        {!commentsLoading[post.postid] && postComments.length === 0 && (
                            <div className="comments-empty-state">
                                <div className="empty-icon">💬</div>
                                <p>No comments yet</p>
                                <span>Be the first to share your thoughts!</span>
                            </div>
                        )}
                        {postComments.map(comment => {
                            const isCommentOwner = userId && String(comment.user_id) === String(userId);
                            const replyCount = comment.replies ? comment.replies.length : 0;
                            const showReplies = expandedReplies[comment.comment_id];

                            return (
                                <div key={comment.comment_id} className="comment-card">
                                    <div className="comment-main">
                                        <img
                                            src={comment.user_avatar || p}
                                            alt={`${comment.username}'s avatar`}
                                            className="comment-avatar"
                                        />
                                        <div className="comment-content">
                                            <div className="comment-bubble">
                                                <div className="comment-header-info">
                                                    <span className="comment-author">{comment.username}</span>
                                                    {comment.timestamp && (
                                                        <span className="comment-time">{comment.timestamp}</span>
                                                    )}
                                                </div>
                                                <p className="comment-text">{comment.comment_text}</p>
                                            </div>
                                            <div className="comment-footer">
                                                <button
                                                    className="comment-action-btn reply-btn"
                                                    onClick={() => onToggleReply(comment.comment_id)}
                                                    type="button"
                                                >
                                                    Reply
                                                </button>
                                                {replyCount > 0 && (
                                                    <button
                                                        className="comment-action-btn view-replies-btn"
                                                        onClick={() => onToggleRepliesVisibility(comment.comment_id)}
                                                        type="button"
                                                    >
                                                        {showReplies ? 'Hide' : 'View'} {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {isCommentOwner && (
                                            <button
                                                className="comment-menu-btn"
                                                onClick={() => onShowDeleteComment(post.postid, comment.comment_id)}
                                                type="button"
                                                aria-label="Comment options"
                                            >
                                                ⋯
                                            </button>
                                        )}
                                    </div>

                                    {showReplies && comment.replies && comment.replies.length > 0 && (
                                        <div className="replies-container">
                                            {comment.replies.map(reply => {
                                                const isReplyOwner = userId && String(reply.reply_user_id) === String(userId);
                                                return (
                                                    <div key={reply.reply_id} className="reply-card">
                                                        <img
                                                            src={reply.reply_avatar || p}
                                                            alt={`${reply.reply_username}'s avatar`}
                                                            className="reply-avatar"
                                                        />
                                                        <div className="reply-content">
                                                            <div className="reply-bubble">
                                                                <div className="reply-header-info">
                                                                    <span className="reply-author">{reply.reply_username}</span>
                                                                    {reply.timestamp && (
                                                                        <span className="reply-time">{reply.timestamp}</span>
                                                                    )}
                                                                </div>
                                                                <p className="reply-text">{reply.reply_text}</p>
                                                            </div>
                                                        </div>
                                                        {isReplyOwner && (
                                                            <button
                                                                className="reply-menu-btn"
                                                                onClick={() => onShowDeleteReply(reply.reply_id)}
                                                                type="button"
                                                                aria-label="Reply options"
                                                            >
                                                                ⋯
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {activeReplies[comment.comment_id] && (
                                        <div className="reply-input-wrapper">
                                            <ReplyInput
                                                commentid={comment.comment_id}
                                                onReplySubmit={onReplySubmit}
                                                onClose={() => onToggleReply(comment.comment_id)}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="comment-input-section">
                        <CommentInput
                            postid={post.postid}
                            onSubmit={onCommentSubmit}
                            userAvatar={null}
                        />
                    </div>
                </div>
            )}
        </div>
    );
});

// ─────────────────────────────────────────────────────────
// Main Home component
// ─────────────────────────────────────────────────────────

function Home() {
    const COMMENT_CACHE_TTL_MS = 30 * 1000;
    const [posts, setPosts] = useState([]);
    const [likeCount, setLikeCount] = useState({});
    const [showComments, setShowComments] = useState({});
    const [comments, setComments] = useState({});
    const [commentsLoading, setCommentsLoading] = useState({});
    const [userId, setUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeReplies, setActiveReplies] = useState({});
    const [expandedReplies, setExpandedReplies] = useState({});
    const [profileImages, setProfileImages] = useState({});
    const [replyViewCounts, setReplyViewCounts] = useState({});
    const [replyLikeCounts, setReplyLikeCounts] = useState({});
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        type: null,
        id: null,
        postid: null,
    });
    const [followStatus, setFollowStatus] = useState({});
    const [followText, setFollowText] = useState({});

    const navigate = useNavigate();
    const { postid: targetPostId } = useParams();
    const postRefs = useRef({});

    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

    const commentsFetchedAtRef = useRef({});

    const postsCache = useContext(PostsCacheContext);


    // ── Fetch posts with cache ───────────────────────────
    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUserId = localStorage.getItem('user_id');
        if (storedUserId) setUserId(storedUserId);

        const applyData = (data) => {
            if (!Array.isArray(data)) return;
            const initialLikeCounts = {};
            const initialFollowStatus = {};
            const initialFollowText = {};
            const images = {};
            data.forEach(post => {
                initialLikeCounts[post.postid] = post.likes || 0;
                initialFollowStatus[post.page_id] = post.is_following;
                initialFollowText[post.page_id] = post.is_following ? "✓ Following" : "+ Follow";
                images[post.page_id] = post.profile_image_url;
            });
            setPosts(data);
            setLikeCount(initialLikeCounts);
            setFollowStatus(initialFollowStatus);
            setFollowText(initialFollowText);
            setProfileImages(images);
        };

        const memCache = postsCache.current;
        const ssCache = (() => {
            try {
                const raw = sessionStorage.getItem('posts_cache');
                return raw ? JSON.parse(raw) : null;
            } catch { return null; }
        })();

        const cached = memCache || ssCache;
        if (cached) {
            applyData(cached);
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }

        fetch(`${API_URL}/api/posts_home`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(data => {
                postsCache.current = data;
                try { sessionStorage.setItem('posts_cache', JSON.stringify(data)); } catch {}
                applyData(data);
            })
            .catch(error => console.error("Error loading home:", error))
            .finally(() => setIsLoading(false));
    }, [API_URL]);

    // ── Scroll to targeted post ──────────────────────────
    useEffect(() => {
        if (!targetPostId) return;

        const interval = setInterval(() => {
            const targetElement = postRefs.current[targetPostId];
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                targetElement.classList.add('highlight-post');
                setTimeout(() => targetElement.classList.remove('highlight-post'), 3000);
                clearInterval(interval);
            }
        }, 300);

        setShowComments(prev => ({ ...prev, [targetPostId]: true }));
        fetchComments(targetPostId);

        return () => clearInterval(interval);
    }, [targetPostId]);

    // ── Comments ─────────────────────────────────────────
    const fetchComments = async (postid, forceRefresh = false) => {
        const hasCachedComments = Array.isArray(comments[postid]);
        const lastFetched = commentsFetchedAtRef.current[postid] || 0;
        const cacheStillFresh = (Date.now() - lastFetched) < COMMENT_CACHE_TTL_MS;
        if (!forceRefresh && hasCachedComments && cacheStillFresh) return;

        setCommentsLoading(prev => ({ ...prev, [postid]: true }));
        try {
            const response = await fetch(`${API_URL}/page/post/commentshome/${postid}`);
            if (!response.ok) return;
            const data = await response.json();

            const repliesState = {};
            const expandedState = {};
            const initialReplyViewCounts = {};
            const initialReplyLikeCounts = {};

            data.comments.forEach(comment => {
                repliesState[comment.comment_id] = false;
                expandedState[comment.comment_id] = false;
                if (comment.replies) {
                    comment.replies.forEach(reply => {
                        initialReplyViewCounts[reply.reply_id] = reply.view_count || 0;
                        initialReplyLikeCounts[reply.reply_id] = reply.like_count || 0;
                    });
                }
            });

            setComments(prev => ({ ...prev, [postid]: data.comments }));
            commentsFetchedAtRef.current[postid] = Date.now();
            setActiveReplies(prev => ({ ...prev, ...repliesState }));
            setExpandedReplies(prev => ({ ...prev, ...expandedState }));
            setReplyViewCounts(prev => ({ ...prev, ...initialReplyViewCounts }));
            setReplyLikeCounts(prev => ({ ...prev, ...initialReplyLikeCounts }));
        } catch (error) {
            console.error("Error fetching comments:", error);
        } finally {
            setCommentsLoading(prev => ({ ...prev, [postid]: false }));
        }
    };

    // ── Handlers (stable with useCallback) ───────────────
    const handleLikeClick = useCallback(async (postid) => {
        try {
            const response = await fetch(`${API_URL}/page/post/like_home/${postid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId })
            });
            if (response.ok) {
                const data = await response.json();
                setLikeCount(prev => ({ ...prev, [postid]: data.likes }));
            }
        } catch (error) {
            console.error('Error liking post:', error);
        }
    }, [API_URL, userId]);

    const handleShowComments = useCallback(async (postid) => {
        setShowComments(prev => {
            const next = !prev[postid];
            if (next) fetchComments(postid);
            return { ...prev, [postid]: next };
        });
    }, [comments]);

    const handleCommentSubmit = useCallback(async (postid, commentText) => {
        try {
            const formData = new FormData();
            formData.append('postid', postid);
            formData.append('user_id', userId);
            formData.append('comment_text', commentText);

            const response = await fetch(`${API_URL}/page/post/commenthome`, {
                method: 'POST',
                body: formData,
            });
            if (response.ok) await fetchComments(postid, true);
        } catch (error) {
            console.error("Error adding comment:", error);
        }
    }, [API_URL, userId]);

    const handleReplySubmit = useCallback(async (commentid, replyText) => {
        try {
            const formData = new FormData();
            formData.append('comment_id', commentid);
            formData.append('user_id', userId);
            formData.append('reply_text', replyText);

            const response = await fetch(`${API_URL}/page/post/replyhome`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const postid = Object.keys(comments).find(pid =>
                    comments[pid].some(c => c.comment_id === commentid)
                );
                await fetchComments(postid, true);
                setActiveReplies(prev => ({ ...prev, [commentid]: false }));
                setExpandedReplies(prev => ({ ...prev, [commentid]: true }));
            }
        } catch (error) {
            console.error("Error adding reply:", error);
        }
    }, [API_URL, userId, comments]);

    const handleDeleteComment = useCallback(async (postid, commentid) => {
        try {
            const response = await fetch(`${API_URL}/api/delete_comment/${commentid}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId })
            });
            if (response.ok) await fetchComments(postid, true);
        } catch (error) {
            console.error("Error deleting comment:", error);
        }
    }, [API_URL, userId]);

    const handleDeleteReply = useCallback(async (replyid) => {
        try {
            const response = await fetch(`${API_URL}/api/delete_reply/${replyid}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId })
            });

            if (response.ok) {
                const postid = Object.keys(comments).find(pid =>
                    comments[pid].some(c => c.replies?.find(r => r.reply_id === replyid))
                );
                await fetchComments(postid, true);
            }
        } catch (error) {
            console.error("Error deleting reply:", error);
        }
    }, [API_URL, userId, comments]);

    const handleFollowClick = useCallback(async (pageId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) { navigate('/login'); return; }

            const response = await fetch(`${API_URL}/api/follow`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ user_id: userId, page_id: pageId }),
            });

            if (response.ok) {
                const data = await response.json();
                setFollowStatus(prev => ({ ...prev, [pageId]: data.following }));
                setFollowText(prev => ({ ...prev, [pageId]: data.following ? "✓ Following" : "+ Follow" }));
            }
        } catch (error) {
            console.error("Error:", error.message);
        }
    }, [API_URL, userId, navigate]);

    const showDeleteCommentConfirmation = useCallback((postid, commentid) => {
        setDeleteConfirmation({ show: true, type: 'comment', id: commentid, postid });
    }, []);

    const showDeleteReplyConfirmation = useCallback((replyid) => {
        setDeleteConfirmation({ show: true, type: 'reply', id: replyid, postid: null });
    }, []);

    const toggleReply = useCallback((commentid) => {
        setActiveReplies(prev => ({ ...prev, [commentid]: !prev[commentid] }));
    }, []);

    const toggleRepliesVisibility = useCallback((commentid) => {
        setExpandedReplies(prev => ({ ...prev, [commentid]: !prev[commentid] }));
    }, []);

    return (
        <>
            <Header />
            {deleteConfirmation.show && (
                <div className="confirmation-dialog-overlay">
                    <div className="confirmation-dialog">
                        <h3>Delete {deleteConfirmation.type}?</h3>
                        <p>Are you sure you want to delete this {deleteConfirmation.type}? This action cannot be undone.</p>
                        <div className="confirmation-buttons">
                            <button
                                className="cancel-btn"
                                onClick={() => setDeleteConfirmation({ show: false })}
                            >
                                Cancel
                            </button>
                            <button
                                className="delete-btn"
                                onClick={() => {
                                    if (deleteConfirmation.type === 'comment') {
                                        handleDeleteComment(deleteConfirmation.postid, deleteConfirmation.id);
                                    } else {
                                        handleDeleteReply(deleteConfirmation.id);
                                    }
                                    setDeleteConfirmation({ show: false });
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="linkedin-feed">
                {isLoading && (!posts || posts.length === 0) ? (
                    // Skeleton cards show instantly — no blank screen while posts load
                    <div className="loading-container-home">
                        {[1,2,3].map(i => (
                            <div key={i} className="linkedin-post skeleton-post" style={{opacity:0.4}}>
                                <div style={{height:60,background:"#e0e0e0",borderRadius:8,marginBottom:12}}/>
                                <div style={{height:120,background:"#e0e0e0",borderRadius:8,marginBottom:8}}/>
                                <div style={{height:20,background:"#e0e0e0",borderRadius:4,width:"60%"}}/>
                            </div>
                        ))}
                    </div>
                ) : Array.isArray(posts) && posts.length > 0 ? (
                    posts.map(post => (
                        <Post
                            key={post.postid}
                            post={post}
                            likeCount={likeCount}
                            followText={followText}
                            profileImages={profileImages}
                            showComments={showComments}
                            comments={comments}
                            commentsLoading={commentsLoading}
                            activeReplies={activeReplies}
                            expandedReplies={expandedReplies}
                            userId={userId}
                            onLike={handleLikeClick}
                            onFollow={handleFollowClick}
                            onShowComments={handleShowComments}
                            onCommentSubmit={handleCommentSubmit}
                            onReplySubmit={handleReplySubmit}
                            onToggleReply={toggleReply}
                            onToggleRepliesVisibility={toggleRepliesVisibility}
                            onShowDeleteComment={showDeleteCommentConfirmation}
                            onShowDeleteReply={showDeleteReplyConfirmation}
                            postRef={(el) => { if (el) postRefs.current[post.postid] = el; }}
                        />
                    ))
                ) : (
                    // ── CHANGED: empty state now prompts to create a page ──
                    <div className="empty-posts">
                        <i className="fa-solid fa-newspaper empty-posts-icon"></i>
                        <h3>No Posts Available</h3>
                        <p>Create your page and start posting!</p>
                        <button
                            className="update-location-btn"
                            onClick={() => navigate('/pages')}
                        >
                            Create Page
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}

export default Home;